import { useState, useEffect, useContext } from "react";
import { 
  Card, 
  Typography, 
  Table, 
  Input, 
  Select, 
  DatePicker, 
  Button, 
  Space, 
  Tag, 
  Badge, 
  Row, 
  Col, 
  Statistic,
  Progress,
  Alert,
  Tooltip,
  Switch,
  Divider,
  Modal,
  Descriptions,
  Timeline,
  Avatar
} from "antd";
import { 
  SearchOutlined, 
  FilterOutlined, 
  ReloadOutlined, 
  DownloadOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  BugOutlined,
  UserOutlined,
  ClockCircleOutlined,
  SettingOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined
} from "@ant-design/icons";
import { ThemeContext } from "../../context/ThemeContext";
import "./AdminLogs.css";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const AdminLogs = () => {
  const { isDark } = useContext(ThemeContext);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("all");
  const [selectedModule, setSelectedModule] = useState("all");
  const [dateRange, setDateRange] = useState(null);
  const [realTimeMonitoring, setRealTimeMonitoring] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [logDetailVisible, setLogDetailVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  // Mock data for logs
  const mockLogs = [
    {
      id: 1,
      timestamp: "2024-01-15 14:30:25",
      level: "ERROR",
      module: "Authentication",
      message: "Failed login attempt for user admin@example.com",
      user: "admin@example.com",
      ip: "192.168.1.100",
      sessionId: "sess_123456789",
      details: "Invalid password provided",
      stackTrace: "Error: Invalid credentials\n    at AuthService.login (/app/services/auth.js:45:12)\n    at LoginController.handle (/app/controllers/login.js:23:8)"
    },
    {
      id: 2,
      timestamp: "2024-01-15 14:29:18",
      level: "INFO",
      module: "User Management",
      message: "New user created successfully",
      user: "system",
      ip: "192.168.1.50",
      sessionId: "sess_987654321",
      details: "User john.doe@example.com created with role 'user'",
      stackTrace: null
    },
    {
      id: 3,
      timestamp: "2024-01-15 14:28:45",
      level: "WARNING",
      module: "Database",
      message: "Database connection pool reaching capacity",
      user: "system",
      ip: "192.168.1.1",
      sessionId: "sess_system",
      details: "Connection pool at 85% capacity (85/100 connections)",
      stackTrace: null
    },
    {
      id: 4,
      timestamp: "2024-01-15 14:27:32",
      level: "DEBUG",
      module: "API",
      message: "API request processed",
      user: "api_user",
      ip: "10.0.0.5",
      sessionId: "sess_api_456",
      details: "GET /api/v1/users - 200 OK - 45ms",
      stackTrace: null
    },
    {
      id: 5,
      timestamp: "2024-01-15 14:26:15",
      level: "ERROR",
      module: "Payment",
      message: "Payment processing failed",
      user: "customer@example.com",
      ip: "203.0.113.10",
      sessionId: "sess_pay_789",
      details: "Stripe API error: card_declined",
      stackTrace: "Error: Payment failed\n    at PaymentService.process (/app/services/payment.js:67:15)\n    at PaymentController.handle (/app/controllers/payment.js:34:12)"
    }
  ];

  useEffect(() => {
    loadLogs();
    if (realTimeMonitoring) {
      const interval = setInterval(() => {
        // Simulate real-time log updates
        const newLog = {
          id: Date.now(),
          timestamp: new Date().toLocaleString(),
          level: ["INFO", "WARNING", "ERROR", "DEBUG"][Math.floor(Math.random() * 4)],
          module: ["Authentication", "User Management", "Database", "API", "Payment"][Math.floor(Math.random() * 5)],
          message: "Real-time log entry",
          user: "system",
          ip: "192.168.1.1",
          sessionId: "sess_realtime",
          details: "Auto-generated log entry for monitoring",
          stackTrace: null
        };
        setLogs(prev => [newLog, ...prev.slice(0, 99)]);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [realTimeMonitoring]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setLogs(mockLogs);
      setPagination(prev => ({ ...prev, total: mockLogs.length }));
    } catch (error) {
      console.error("Failed to load logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case "ERROR": return "red";
      case "WARNING": return "orange";
      case "INFO": return "blue";
      case "DEBUG": return "green";
      default: return "default";
    }
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case "ERROR": return <ExclamationCircleOutlined />;
      case "WARNING": return <WarningOutlined />;
      case "INFO": return <InfoCircleOutlined />;
      case "DEBUG": return <BugOutlined />;
      default: return <InfoCircleOutlined />;
    }
  };

  const handleSearch = () => {
    // Implement search logic
    console.log("Searching logs with:", { searchText, selectedLevel, selectedModule, dateRange });
  };

  const handleExport = () => {
    // Implement export logic
    console.log("Exporting logs...");
  };

  const showLogDetail = (log) => {
    setSelectedLog(log);
    setLogDetailVisible(true);
  };

  const columns = [
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 150,
      render: (text) => (
        <div className="timestamp-cell">
          <ClockCircleOutlined className="timestamp-icon" />
          <span>{text}</span>
        </div>
      )
    },
    {
      title: "Level",
      dataIndex: "level",
      key: "level",
      width: 100,
      render: (level) => (
        <Tag color={getLevelColor(level)} icon={getLevelIcon(level)}>
          {level}
        </Tag>
      )
    },
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      width: 120,
      render: (module) => (
        <Tag color="blue">
          <SettingOutlined /> {module}
        </Tag>
      )
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      ellipsis: true,
      render: (text, record) => (
        <div className="message-cell">
          <Text strong>{text}</Text>
          {record.details && (
            <Text type="secondary" className="log-details">
              {record.details}
            </Text>
          )}
        </div>
      )
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      width: 120,
      render: (user) => (
        <div className="user-cell">
          <Avatar size="small" icon={<UserOutlined />} />
          <span>{user}</span>
        </div>
      )
    },
    {
      title: "IP Address",
      dataIndex: "ip",
      key: "ip",
      width: 120,
      render: (ip) => <Text code>{ip}</Text>
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => showLogDetail(record)}
              size="small"
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const logStats = {
    total: logs.length,
    error: logs.filter(log => log.level === "ERROR").length,
    warning: logs.filter(log => log.level === "WARNING").length,
    info: logs.filter(log => log.level === "INFO").length,
    debug: logs.filter(log => log.level === "DEBUG").length
  };

  return (
    <div className={`admin-logs-container ${isDark ? 'dark' : ''}`}>
      {/* Hero Section */}
      <div
        className="admin-logs-hero"
        style={{
          background: 'linear-gradient(90deg, #3AA015 0%, #F49A24 50%, rgba(255, 0, 4, 0.61) 100%)',
          borderRadius: 18,
          padding: '2px',
          marginBottom: 32,
          position: 'relative',
        }}
      >
        <div
          style={{
            background: 'transparent',
            borderRadius: 16,
            padding: "48px 48px 40px 48px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            position: 'relative',
            overflow: 'hidden',
            minHeight: 180,
            width: '100%',
            height: '100%',
          }}
        >
          {/* Overlay for better text readability */}
          {isDark && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 100%)',
                borderRadius: 16,
              }}
            />
          )}
          
          {/* Content */}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <Title
                level={1}
                style={{
                  margin: 0,
                  marginBottom: 12,
                  fontWeight: 800,
                  fontSize: 42,
                  color: isDark ? "#ffffff" : "#1a1a1a",
                  textShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none',
                }}
              >
                System Logs
              </Title>
              <Text 
                style={{ 
                  fontSize: 20, 
                  color: isDark ? "rgba(255, 255, 255, 0.9)" : "#4a4a4a",
                  fontWeight: 400,
                  lineHeight: 1.4,
                  textShadow: isDark ? '0 1px 2px rgba(0, 0, 0, 0.3)' : 'none',
                }}
              >
                Monitor and analyze system activities, errors, and performance metrics
              </Text>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Switch
                checked={realTimeMonitoring}
                onChange={setRealTimeMonitoring}
                checkedChildren={<PlayCircleOutlined />}
                unCheckedChildren={<PauseCircleOutlined />}
                style={{
                  borderRadius: 8,
                }}
              />
              <Text style={{ color: isDark ? "rgba(255, 255, 255, 0.9)" : "#4a4a4a", fontWeight: 500 }}>Real-time Monitoring</Text>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} md={6}>
          <div
            style={{
              background: isDark ? "#2a2a3e" : "#fff",
              border: isDark ? "1px solid #3a3a4e" : "1px solid #000",
              borderRadius: 12,
              boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.06)",
              padding: "20px",
              position: "relative",
              height: 200,
              overflow: "hidden"
            }}
          >
            <div style={{ 
              position: "absolute",
              top: "16px",
              left: "16px",
              fontSize: 19,
              fontWeight: 600,
              fontStyle: "Semi Bold",
              color: isDark ? "#ffffff" : "#000",
              zIndex: 2
            }}>
              Total Logs
            </div>
            <div style={{ 
              position: "absolute",
              bottom: "20px",
              left: "16px",
              fontSize: 36, 
              fontWeight: 700, 
              fontStyle: "bold",
              color: isDark ? "#ffffff" : "#000",
              lineHeight: 1,
              zIndex: 1
            }}>
              {logStats.total}
            </div>
            <div style={{ 
              position: "absolute",
              bottom: "16px",
              right: "16px",
              fontSize: 12, 
              color: isDark ? "#73d13d" : "#52c41a",
              fontWeight: 500
            }}>
              +12%
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div
            style={{
              background: isDark ? "#2a2a3e" : "#fff",
              border: isDark ? "1px solid #3a3a4e" : "1px solid #000",
              borderRadius: 12,
              boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.06)",
              padding: "20px",
              position: "relative",
              height: 200,
              overflow: "hidden"
            }}
          >
            <div style={{ 
              position: "absolute",
              top: "16px",
              left: "16px",
              fontSize: 19,
              fontWeight: 600,
              fontStyle: "Semi Bold",
              color: isDark ? "#ffffff" : "#000",
              zIndex: 2
            }}>
              Errors
            </div>
            <div style={{ 
              position: "absolute",
              bottom: "20px",
              left: "16px",
              fontSize: 36, 
              fontWeight: 700, 
              fontStyle: "bold",
              color: isDark ? "#ffffff" : "#000",
              lineHeight: 1,
              zIndex: 1
            }}>
              {logStats.error}
            </div>
            <div style={{ 
              position: "absolute",
              bottom: "16px",
              right: "16px",
              fontSize: 12, 
              color: isDark ? "#ff7875" : "#ff4d4f",
              fontWeight: 500
            }}>
              +8%
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div
            style={{
              background: isDark ? "#2a2a3e" : "#fff",
              border: isDark ? "1px solid #3a3a4e" : "1px solid #000",
              borderRadius: 12,
              boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.06)",
              padding: "20px",
              position: "relative",
              height: 200,
              overflow: "hidden"
            }}
          >
            <div style={{ 
              position: "absolute",
              top: "16px",
              left: "16px",
              fontSize: 19,
              fontWeight: 600,
              fontStyle: "Semi Bold",
              color: isDark ? "#ffffff" : "#000",
              zIndex: 2
            }}>
              Warnings
            </div>
            <div style={{ 
              position: "absolute",
              bottom: "20px",
              left: "16px",
              fontSize: 36, 
              fontWeight: 700, 
              fontStyle: "bold",
              color: isDark ? "#ffffff" : "#000",
              lineHeight: 1,
              zIndex: 1
            }}>
              {logStats.warning}
            </div>
            <div style={{ 
              position: "absolute",
              bottom: "16px",
              right: "16px",
              fontSize: 12, 
              color: isDark ? "#ffc53d" : "#faad14",
              fontWeight: 500
            }}>
              -5%
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div
            style={{
              background: isDark ? "#2a2a3e" : "#fff",
              border: isDark ? "1px solid #3a3a4e" : "1px solid #000",
              borderRadius: 12,
              boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.06)",
              padding: "20px",
              position: "relative",
              height: 200,
              overflow: "hidden"
            }}
          >
            <div style={{ 
              position: "absolute",
              top: "16px",
              left: "16px",
              fontSize: 19,
              fontWeight: 600,
              fontStyle: "Semi Bold",
              color: isDark ? "#ffffff" : "#000",
              zIndex: 2
            }}>
              Info
            </div>
            <div style={{ 
              position: "absolute",
              bottom: "20px",
              left: "16px",
              fontSize: 36, 
              fontWeight: 700, 
              fontStyle: "bold",
              color: isDark ? "#ffffff" : "#000",
              lineHeight: 1,
              zIndex: 1
            }}>
              {logStats.info}
            </div>
            <div style={{ 
              position: "absolute",
              bottom: "16px",
              right: "16px",
              fontSize: 12, 
              color: isDark ? "#73d13d" : "#52c41a",
              fontWeight: 500
            }}>
              +15%
            </div>
          </div>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="filters-card">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={6}>
            <Input
              placeholder="Search logs..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>
          <Col xs={24} md={4}>
            <Select
              placeholder="Log Level"
              value={selectedLevel}
              onChange={setSelectedLevel}
              style={{ width: '100%' }}
            >
              <Option value="all">All Levels</Option>
              <Option value="ERROR">Error</Option>
              <Option value="WARNING">Warning</Option>
              <Option value="INFO">Info</Option>
              <Option value="DEBUG">Debug</Option>
            </Select>
          </Col>
          <Col xs={24} md={4}>
            <Select
              placeholder="Module"
              value={selectedModule}
              onChange={setSelectedModule}
              style={{ width: '100%' }}
            >
              <Option value="all">All Modules</Option>
              <Option value="Authentication">Authentication</Option>
              <Option value="User Management">User Management</Option>
              <Option value="Database">Database</Option>
              <Option value="API">API</Option>
              <Option value="Payment">Payment</Option>
            </Select>
          </Col>
          <Col xs={24} md={6}>
            <RangePicker
              placeholder={["Start Date", "End Date"]}
              value={dateRange}
              onChange={setDateRange}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={4}>
            <Space>
              <Button 
                type="primary" 
                icon={<FilterOutlined />}
                onClick={handleSearch}
              >
                Filter
              </Button>
              <Button 
                icon={<ReloadOutlined />}
                onClick={loadLogs}
                loading={loading}
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Logs Table */}
      <Card className="logs-table-card">
        <div className="table-header">
          <Space>
            <Text strong>System Logs</Text>
            <Badge count={logs.length} showZero />
          </Space>
          <Button 
            type="primary" 
            icon={<DownloadOutlined />}
            onClick={handleExport}
          >
            Export Logs
          </Button>
        </div>
        
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} logs`
          }}
          onChange={(pagination) => setPagination(pagination)}
          className="custom-table"
        />
      </Card>

      {/* Log Detail Modal */}
      <Modal
        title="Log Details"
        open={logDetailVisible}
        onCancel={() => setLogDetailVisible(false)}
        footer={null}
        width={800}
        className="log-detail-modal"
      >
        {selectedLog && (
          <div className="log-detail-content">
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Timestamp" span={2}>
                {selectedLog.timestamp}
              </Descriptions.Item>
              <Descriptions.Item label="Level">
                <Tag color={getLevelColor(selectedLog.level)} icon={getLevelIcon(selectedLog.level)}>
                  {selectedLog.level}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Module">
                <Tag color="blue">{selectedLog.module}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Message" span={2}>
                {selectedLog.message}
              </Descriptions.Item>
              <Descriptions.Item label="User">
                {selectedLog.user}
              </Descriptions.Item>
              <Descriptions.Item label="IP Address">
                <Text code>{selectedLog.ip}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Session ID">
                <Text code>{selectedLog.sessionId}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Details" span={2}>
                {selectedLog.details || "No additional details"}
              </Descriptions.Item>
            </Descriptions>
            
            {selectedLog.stackTrace && (
              <div className="stack-trace-section">
                <Title level={5}>Stack Trace</Title>
                <div className="stack-trace">
                  <pre>{selectedLog.stackTrace}</pre>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminLogs; 